"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.merchantApplicationIdFor = exports.MerchantApplicationSchema = exports.MerchantQuoteSchema = exports.MERCHANT_QUOTE_STATUSES = exports.MERCHANT_THREAD_CAP = exports.MerchantMessageSchema = exports.MERCHANT_MESSAGE_ROLES = exports.TerminalAssignmentSchema = exports.VarSheetSchema = exports.MerchantRatesSchema = exports.MerchantRateLineSchema = exports.MerchantProfileSchema = exports.MERCHANT_ACCESS_LOG_CAP = exports.MerchantAccessEntrySchema = exports.MerchantDocumentSchema = exports.MERCHANT_SOURCES = exports.MERCHANT_APPLICATION_OPEN_STATUSES = exports.MERCHANT_APPLICATION_STATUSES = void 0;
exports.dispatchReadiness = dispatchReadiness;
exports.activeSheetFor = activeSheetFor;
exports.merchantStatusSummary = merchantStatusSummary;
exports.isoMaySeeApplication = isoMaySeeApplication;
exports.tenantOutstandingDocuments = tenantOutstandingDocuments;
exports.visibleMessages = visibleMessages;
exports.activeQuote = activeQuote;
exports.quoteAwaitingTenant = quoteAwaitingTenant;
exports.mayAddressAudience = mayAddressAudience;
const zod_1 = require("zod");
/**
 * MERCHANT APPLICATION — the card-processing track.
 *
 * ONE PER LOCATION, not per tenant. A merchant account is boarded against a
 * legal entity at an address with its own MCC, and a tenant with five stores
 * may well have five of them — possibly with different processors. Keying it
 * to the location is what makes "store 3 is approved, store 4 is in
 * underwriting" expressible at all.
 *
 * WHAT THIS IS NOT. It is not RubyTill's own compliance pack (EIN document,
 * articles, licence, DBA) — that lives on the location-billing request and is
 * chased by RubyTill. The two tracks look similar and are constantly confused;
 * they have different owners, different documents and different consequences:
 *
 *   compliance pack  → can we INVOICE this business?      never blocks anything
 *   this record      → can they TAKE CARD PAYMENTS?       blocks device dispatch
 *
 * Approved plus a VAR sheet is what lets a terminal be programmed and shipped.
 * Nothing here ever gates software access: the tenant has been selling since
 * the day they paid.
 */
/**
 * The status machine. Forward-only in normal use, but `info_requested` and
 * `declined` can both go back to `submitted` when the customer answers or the
 * ISO re-files, so this is not a strict ladder.
 */
exports.MERCHANT_APPLICATION_STATUSES = [
    /** Opened automatically when the location was created. Nobody has answered yet. */
    'draft',
    /**
     * "I already process cards." The tenant brought their own merchant account,
     * so there is no underwriting to do and no ISO to assign — the only thing
     * outstanding is a VAR sheet per payment device, which they upload from
     * their own status page once they have paid. Straight to `approved` from
     * here, when the first VAR lands.
     */
    'existing_merchant',
    /** A super admin has picked an ISO partner. Theirs to work now. */
    'assigned',
    /** The ISO has sent it to the processor. */
    'submitted',
    /** Underwriting wants something. `outstandingDocuments` says what. */
    'info_requested',
    /** Boarded. A MID exists; a VAR sheet may not be attached yet. */
    'approved',
    /** Refused, with a reason the tenant can act on. */
    'declined',
    /** Given up on — the tenant is running cash-only or brought their own processor. */
    'abandoned',
];
/** Statuses in which the ISO still has work to do. Drives their Orders list. */
exports.MERCHANT_APPLICATION_OPEN_STATUSES = [
    'assigned', 'submitted', 'info_requested',
];
/**
 * WHICH WAY THIS LOCATION GOES, and the question the wizard asks to find out:
 * "do you already take card payments, or do you need us to set that up?"
 *
 * It is asked once per location, because the answer really can differ between
 * stores — an owner opening a second shop often keeps the processor they know.
 * `undefined` means nobody has been asked yet, which is why `draft` exists.
 */
exports.MERCHANT_SOURCES = ['existing', 'new'];
/**
 * A document the processor asked for, on top of RubyTill's own pack.
 *
 * `storageKey` is an S3 key, never a URL: a URL in a record is a URL that
 * leaks when the record is logged, and these carry bank details.
 */
exports.MerchantDocumentSchema = zod_1.z.object({
    documentId: zod_1.z.string().min(1).max(64),
    name: zod_1.z.string().min(1).max(160),
    status: zod_1.z.enum(['requested', 'uploaded', 'accepted', 'rejected']),
    /** Why it was rejected, or what exactly is wanted. Shown to the tenant verbatim. */
    comment: zod_1.z.string().max(1000).optional(),
    storageKey: zod_1.z.string().max(1024).optional(),
    fileName: zod_1.z.string().max(255).optional(),
    contentType: zod_1.z.string().max(160).optional(),
    requestedBy: zod_1.z.string().max(160).optional(),
    requestedAt: zod_1.z.number().optional(),
    uploadedAt: zod_1.z.number().optional(),
    /**
     * SENSITIVE. A voided check or a bank statement carries an account and a
     * routing number. Reads of these are attributed in `accessLog` and they are
     * never included in a list response — only when one document is fetched by
     * id, by somebody entitled to it.
     */
    sensitive: zod_1.z.boolean().optional(),
    /**
     * THE DEVICE THIS DOCUMENT IS ABOUT, when it is about one.
     *
     * A processor commonly asks for something per reader — an attestation, a
     * signed terminal form, a photo of the installed device — and a flat list
     * gave no way to say which of three readers it belonged to. Additive and
     * optional: a document about the BUSINESS (the usual case) carries none,
     * which is every document written before this field existed.
     *
     * Never a gate. `dispatchReadiness` counts sheets and TIDs, not documents,
     * and attaching paperwork to a reader must not quietly hold a shipment.
     */
    unitId: zod_1.z.string().max(64).optional(),
    uploadedBy: zod_1.z.string().max(160).optional(),
});
/**
 * Every look at a sensitive document, kept on the record itself.
 *
 * On the record rather than in a log sink on purpose: the tenant is shown who
 * opened their bank details and when, and a promise you cannot render to the
 * person it is about is not a promise.
 */
exports.MerchantAccessEntrySchema = zod_1.z.object({
    at: zod_1.z.number(),
    /** The partner or admin email. Never an opaque id — this is read by humans. */
    by: zod_1.z.string().max(160),
    actor: zod_1.z.enum(['iso', 'super_admin', 'tenant']),
    action: zod_1.z.enum(['viewed', 'downloaded', 'uploaded', 'requested', 'removed']),
    documentId: zod_1.z.string().max(64).optional(),
});
/** How many access entries a record keeps. Same rationale as SKU_HISTORY_CAP. */
exports.MERCHANT_ACCESS_LOG_CAP = 200;
/**
 * The underwriting facts RubyTill does not already know.
 *
 * Everything the tenant has already told us — legal entity, EIN, address,
 * business type — is read off the tenant and the location and is NOT copied
 * here. A second copy is a second thing to keep in step, and the one that goes
 * stale is always the copy.
 */
exports.MerchantProfileSchema = zod_1.z.object({
    /** Merchant category code, e.g. `5993` for tobacco. Decides who can board it. */
    mccCode: zod_1.z.string().max(8).optional(),
    estimatedMonthlyVolumeCents: zod_1.z.number().int().nonnegative().optional(),
    averageTicketCents: zod_1.z.number().int().nonnegative().optional(),
    /** 0-100. The rest is assumed card-not-present. */
    cardPresentPercent: zod_1.z.number().min(0).max(100).optional(),
    yearsTrading: zod_1.z.number().min(0).max(200).optional(),
    owners: zod_1.z.array(zod_1.z.object({
        name: zod_1.z.string().max(160),
        ownershipPercent: zod_1.z.number().min(0).max(100).optional(),
        title: zod_1.z.string().max(120).optional(),
    })).max(10).optional(),
    /**
     * Bank details are DELIBERATELY not here. They arrive as a voided check
     * document, which is stored, access-logged and never returned in a list.
     * Do not add an accountNumber field to this schema.
     */
});
/**
 * A VAR sheet — what the processor issues on approval, and what a terminal has
 * to be programmed with before it is worth shipping.
 *
 * A RECORD HOLDS SEVERAL. One VAR usually carries one TID per terminal, but a
 * tenant who brought their own processing may hand over a separate sheet per
 * device, and a large estate can be split across more than one MID. So an
 * application owns `varSheets[]`, each unit's TID names the `varId` it came
 * from, and neither case needs a special path.
 */
/**
 * ONE LINE OF A PROPOSED RATE — what the partner is quoting for this account.
 *
 * Two numbers and a sentence, because that is how a rate is actually spoken:
 * "2.2% + 10c", "5.5c per transaction, capped". `note` is where the caveat
 * goes — the thing that makes a headline number mean something.
 *
 * DELIBERATELY NOT A PRICE ENGINE. Nothing computes with these; they are the
 * partner's own summary of the schedule they are proposing, shown beside the
 * sheet it belongs to. The authoritative document is still the rate schedule
 * file, for the same reason `MerchantQuoteSchema` is a file: every processor
 * writes one differently and a handful of columns cannot hold a real one.
 */
exports.MerchantRateLineSchema = zod_1.z.object({
    /** e.g. 2.2 for 2.2%. */
    percent: zod_1.z.number().min(0).max(100).optional(),
    /** The per-transaction piece, in cents. e.g. 10 for 10c. */
    perTransactionCents: zod_1.z.number().int().nonnegative().max(100000).optional(),
    /** The partner's own caveat on this line, shown verbatim. */
    note: zod_1.z.string().max(300).optional(),
});
exports.MerchantRatesSchema = zod_1.z.object({
    credit: exports.MerchantRateLineSchema.optional(),
    debit: exports.MerchantRateLineSchema.optional(),
    /** Anything that applies to both — monthly fee, term, early termination. */
    note: zod_1.z.string().max(500).optional(),
});
exports.VarSheetSchema = zod_1.z.object({
    varId: zod_1.z.string().min(1).max(64),
    /** Merchant id. Sensitive-ish: shown to the tenant, never in a public read. */
    mid: zod_1.z.string().max(64),
    processor: zod_1.z.string().max(120).optional(),
    gateway: zod_1.z.string().max(120).optional(),
    mccCode: zod_1.z.string().max(8).optional(),
    fundingNote: zod_1.z.string().max(400).optional(),
    terminalIds: zod_1.z.array(zod_1.z.string().max(64)).max(200).default([]),
    storageKey: zod_1.z.string().max(1024).optional(),
    fileName: zod_1.z.string().max(255).optional(),
    uploadedBy: zod_1.z.string().max(160).optional(),
    /** Who put it there — the tenant themselves on the `existing` path. */
    uploadedByRole: zod_1.z.enum(['iso', 'super_admin', 'tenant']).optional(),
    uploadedAt: zod_1.z.number().optional(),
    /**
     * THE DEVICE THIS SHEET PROGRAMS. Unset until allocation binds it — at
     * checkout no unit exists yet, so a sheet arrives unbound and is bound later.
     * One sheet programs one reader: a sheet already bound elsewhere is not
     * offered for a second.
     */
    unitId: zod_1.z.string().max(64).optional(),
    /**
     * When this sheet stopped being the one in force for its device. Set by
     * binding a replacement, never by deleting: a sheet a reader was actually
     * keyed off is evidence, and `terminalAssignments[].varId` still points at it.
     */
    supersededAt: zod_1.z.number().optional(),
    /**
     * THE RATE THIS ACCOUNT IS BEING PROPOSED, in the partner's own summary.
     * Additive and optional — every sheet written before this has none.
     */
    rates: exports.MerchantRatesSchema.optional(),
});
/**
 * A TID handed to one physical unit. What the dispatch gate counts, and what
 * the device configuration is generated from.
 *
 * `tpn` is Dejavoo's terminal profile number and is what their estate is keyed
 * by; `terminalId` is the processor-side TID. They are not the same field and
 * a device can need both, so both are here rather than one overloaded string.
 */
exports.TerminalAssignmentSchema = zod_1.z.object({
    /** The device unit this belongs to — hardware-service's unit id. */
    unitId: zod_1.z.string().min(1).max(64),
    terminalId: zod_1.z.string().min(1).max(64),
    /** Dejavoo TPN, where the device uses one. */
    tpn: zod_1.z.string().max(64).optional(),
    /** Which sheet this came off. */
    varId: zod_1.z.string().max(64).optional(),
    serial: zod_1.z.string().max(64).optional(),
    assignedAt: zod_1.z.number(),
    assignedBy: zod_1.z.string().max(160).optional(),
    /** Set once the terminal has actually been programmed with these values. */
    configuredAt: zod_1.z.number().optional(),
});
/**
 * WHO A MESSAGE IS FOR.
 *
 * Three parties talk on one application and they must not all hear the same
 * things: the super admin negotiates a rate with the ISO, the ISO asks the
 * tenant for a document, and RubyTill keeps notes neither of them should read.
 * Rather than three threads that have to be read side by side to reconstruct
 * what happened, there is ONE thread and every message names its audience.
 *
 * The author always sees their own message; `audience` says who ELSE does.
 */
exports.MERCHANT_MESSAGE_ROLES = ['super_admin', 'iso', 'tenant'];
exports.MerchantMessageSchema = zod_1.z.object({
    messageId: zod_1.z.string().min(1).max(64),
    at: zod_1.z.number(),
    authorRole: zod_1.z.enum(exports.MERCHANT_MESSAGE_ROLES),
    /** Shown verbatim — "Northbridge · dave@northbridge.co", "Aruna · super admin". */
    authorName: zod_1.z.string().min(1).max(160),
    authorEmail: zod_1.z.string().max(160).optional(),
    body: zod_1.z.string().min(1).max(4000),
    /**
     * Who may read this besides the author. An empty list is a private note:
     * legal, and the reason `super_admin` alone means "internal".
     */
    audience: zod_1.z.array(zod_1.z.enum(exports.MERCHANT_MESSAGE_ROLES)).max(3).default([]),
    /** A document referenced by this message — the quote, a VAR, a request. */
    attachmentId: zod_1.z.string().max(64).optional(),
});
/** How many messages a record keeps. Same rationale as the access log. */
exports.MERCHANT_THREAD_CAP = 500;
/**
 * THE RATE QUOTE, AS A DOCUMENT.
 *
 * Deliberately a file and not a set of fields. Every processor writes a rate
 * schedule differently — interchange-plus, tiered, flat, with a page of
 * per-item fees underneath — and a handful of columns would either fail to
 * hold a real one or quietly drop the terms that matter. What is structured
 * is only what RubyTill needs to act on: has it been sent, and did the
 * merchant agree to it.
 *
 * `summary` is the ISO's own one-line gloss ("2.6% + 10c, $9.95/mo, 24 mo").
 * It is a courtesy for the list screens and is NOT authoritative — the file is.
 */
exports.MERCHANT_QUOTE_STATUSES = ['proposed', 'accepted', 'declined', 'superseded'];
exports.MerchantQuoteSchema = zod_1.z.object({
    quoteId: zod_1.z.string().min(1).max(64),
    status: zod_1.z.enum(exports.MERCHANT_QUOTE_STATUSES),
    /** The ISO's own summary line. Never parsed, never used to price anything. */
    summary: zod_1.z.string().max(400).optional(),
    storageKey: zod_1.z.string().max(1024).optional(),
    fileName: zod_1.z.string().max(255).optional(),
    contentType: zod_1.z.string().max(160).optional(),
    uploadedBy: zod_1.z.string().max(160).optional(),
    uploadedByRole: zod_1.z.enum(['iso', 'super_admin']).optional(),
    uploadedAt: zod_1.z.number(),
    /** Set when the tenant accepts or declines. Only they may. */
    decidedAt: zod_1.z.number().optional(),
    decidedBy: zod_1.z.string().max(160).optional(),
    /** Why they said no. Shown to the ISO so the next quote is a better one. */
    declineReason: zod_1.z.string().max(1000).optional(),
    /**
     * WHICH PARTNER PUT THIS FORWARD. `uploadedBy` is free text and cannot be
     * compared; this can. Optional because quotes predating it exist — and it is
     * added now precisely because they can never be retro-attributed.
     */
    isoId: zod_1.z.string().max(64).optional(),
});
exports.MerchantApplicationSchema = zod_1.z.object({
    tenantId: zod_1.z.string().min(1),
    /** `mapp_<locationId>` — derived, so opening one twice for a store cannot happen. */
    applicationId: zod_1.z.string().min(1),
    locationId: zod_1.z.string().min(1),
    /** Denormalised for list screens ONLY. Never read this to make a decision. */
    locationName: zod_1.z.string().max(200).optional(),
    status: zod_1.z.enum(exports.MERCHANT_APPLICATION_STATUSES),
    /** The partner working it. Absent means it is sitting in the unassigned queue. */
    assignedIsoId: zod_1.z.string().max(64).optional(),
    assignedIsoName: zod_1.z.string().max(200).optional(),
    assignedAt: zod_1.z.number().optional(),
    assignedBy: zod_1.z.string().max(160).optional(),
    /** The answer to "do you already take card payments?". See MERCHANT_SOURCES. */
    merchantSource: zod_1.z.enum(exports.MERCHANT_SOURCES).optional(),
    profile: exports.MerchantProfileSchema.optional(),
    documents: zod_1.z.array(exports.MerchantDocumentSchema).max(60).default([]),
    varSheets: zod_1.z.array(exports.VarSheetSchema).max(50).default([]),
    /** Rate schedules the partner has put forward. Newest last. */
    quoteDocuments: zod_1.z.array(exports.MerchantQuoteSchema).max(20).default([]),
    /** The single conversation, audience-tagged per message. See MerchantMessageSchema. */
    thread: zod_1.z.array(exports.MerchantMessageSchema).max(exports.MERCHANT_THREAD_CAP).default([]),
    terminalAssignments: zod_1.z.array(exports.TerminalAssignmentSchema).max(200).default([]),
    /** Set when the processor refuses. Shown to the tenant, so it must be actionable. */
    declinedReason: zod_1.z.string().max(1000).optional(),
    submittedAt: zod_1.z.number().optional(),
    approvedAt: zod_1.z.number().optional(),
    declinedAt: zod_1.z.number().optional(),
    accessLog: zod_1.z.array(exports.MerchantAccessEntrySchema).max(exports.MERCHANT_ACCESS_LOG_CAP).default([]),
    createdAt: zod_1.z.number(),
    updatedAt: zod_1.z.number().optional(),
});
/** `loc_123` → `mapp_loc_123`. Derived so a store can only ever have one. */
const merchantApplicationIdFor = (locationId) => `mapp_${locationId}`;
exports.merchantApplicationIdFor = merchantApplicationIdFor;
/**
 * Whether every payment unit in an order can be programmed, i.e. whether
 * dispatch may proceed.
 *
 * THE TID IS THE EVIDENCE, not the status. A terminal id only exists because
 * a real VAR sheet was uploaded and attached to that unit, so counting
 * assignments answers the question directly — and it answers it identically
 * for a tenant who brought their own processor and one an ISO boarded. Gating
 * on `status === 'approved'` instead would strand the whole `existing_merchant`
 * path, which never passes through underwriting at all.
 *
 * Counts ASSIGNMENTS, not TIDs on the sheet: a VAR listing six TIDs means
 * nothing if four of them were never attached to a unit.
 *
 * `unitIds` is the order's PAYMENT units only. A receipt printer or a scanner
 * has no TID and must never be held back for one — pass those units in and
 * this will refuse to ship a printer for ever.
 */
function dispatchReadiness(app, unitIds) {
    // Nothing to program is not a blocked order. A software-only or
    // bring-your-own-hardware store ships whatever else is on the order.
    if (unitIds.length === 0)
        return { ready: true, missing: [] };
    if (!app) {
        return {
            ready: false,
            missing: [...unitIds],
            reason: 'No merchant application exists for this location yet.',
        };
    }
    if (app.status === 'declined' || app.status === 'abandoned') {
        return {
            ready: false,
            missing: [...unitIds],
            reason: app.status === 'declined'
                ? 'The merchant application was declined, so these terminals cannot be programmed.'
                : 'This location is not setting up card processing.',
        };
    }
    /*
      A BOUND SHEET FINISHES A READER, and so does a keyed TID.
  
      The sheet is the thing the merchant or the partner actually produces; the
      TID is a number off it. Requiring the number as well made the card lane
      unfinishable on the commonest path we have: a bring-your-own-processor
      merchant hands over their own sheet, no ISO is ever assigned, and the ONLY
      route that could write a terminal assignment (`POST /iso/.../terminals`)
      demands an assigned partner. Nobody could clear the gate, and the order
      could never pack.
  
      STILL PER READER, via `activeSheetFor` — bound to THIS unit and not
      superseded. One sheet somewhere on the application has never been enough
      and is not now: that is how a reader gets credited with another reader's
      paperwork.
  
      WHAT THIS GIVES UP, said plainly: the gate no longer proves a terminal was
      programmed, only that the paperwork authorising it is in force and attached
      to that unit. A Dejavoo whose sheet is bound but which was never registered
      into the location's pool will now pass here. The pool registration is a
      separate stage (`register_emv`) and the order rail shows it; this function
      cannot see the device pool at all, so it cannot be the thing that checks it.
    */
    const missing = unitIds.filter((id) => !(app.terminalAssignments || []).some((t) => t.unitId === id)
        && !activeSheetFor(app.varSheets, id));
    if (missing.length > 0) {
        return {
            ready: false,
            missing,
            reason: `${missing.length} unit${missing.length === 1 ? '' : 's'} still need a VAR sheet bound to them.`,
        };
    }
    return { ready: true, missing: [] };
}
/**
 * THE SHEET THAT PROGRAMS ONE READER, and the only place that is decided.
 *
 * A sheet advances for a device when it is bound to that device and has not
 * been superseded. Everything else — a sheet on file but unbound, a sheet bound
 * to a different reader, a sheet replaced by a later one — advances nothing.
 *
 * This replaces asking "does this application have any sheet at all", which
 * enabled programming on readers nobody had issued a TID for.
 *
 * AT MOST ONE LIVE SHEET PER UNIT is an invariant of the WRITE path, not of
 * this function: binding a sheet to a unit that already has one stamps
 * `supersededAt` on the old. If two live sheets ever reach here the data is
 * already wrong; array order decides, and the test pinning that is a tripwire
 * rather than a feature to rely on.
 */
function activeSheetFor(sheets, unitId) {
    if (!sheets?.length || !unitId)
        return undefined;
    return sheets.find((s) => s.unitId === unitId && s.supersededAt === undefined);
}
/**
 * What to tell the person looking at the order — one sentence, and whose move
 * it is. Used by the tenant's status page, the ISO's Orders list and the
 * super-admin queue, so all three describe the same record the same way.
 */
function merchantStatusSummary(app, paymentUnitIds = []) {
    if (!app || app.status === 'draft') {
        return { headline: 'We have not asked about card payments yet.', waitingOn: 'rubytill' };
    }
    if (app.status === 'declined') {
        return { headline: 'The application was declined.', waitingOn: 'tenant' };
    }
    if (app.status === 'abandoned') {
        return { headline: 'Card processing is not being set up for this store.', waitingOn: 'nobody' };
    }
    const outstanding = tenantOutstandingDocuments(app).length;
    if (outstanding > 0) {
        return {
            headline: `${outstanding} document${outstanding === 1 ? '' : 's'} still needed from you.`,
            waitingOn: 'tenant',
        };
    }
    if (quoteAwaitingTenant(app)) {
        return { headline: 'A rate schedule is waiting for you to accept or decline.', waitingOn: 'tenant' };
    }
    const unprogrammed = dispatchReadiness(app, paymentUnitIds);
    if (!unprogrammed.ready && unprogrammed.missing.length > 0) {
        return app.merchantSource === 'existing'
            ? {
                headline: `Upload the VAR sheet for ${unprogrammed.missing.length} more device${unprogrammed.missing.length === 1 ? '' : 's'} and we will configure them.`,
                waitingOn: 'tenant',
            }
            : { headline: 'Waiting on the VAR sheet from your payments partner.', waitingOn: 'iso' };
    }
    if (app.status === 'existing_merchant') {
        return { headline: 'Your own merchant account — devices configured and ready.', waitingOn: 'nobody' };
    }
    if (app.status === 'submitted') {
        return { headline: 'With the processor for underwriting.', waitingOn: 'processor' };
    }
    if (app.status === 'assigned') {
        return { headline: 'Your payments partner is preparing the application.', waitingOn: 'iso' };
    }
    return { headline: 'Approved. Card payments are live.', waitingOn: 'nobody' };
}
/**
 * May this ISO partner see this application?
 *
 * The ONE place that question is answered. Every ISO-facing endpoint calls it;
 * none re-implements it. Assignment is the whole rule — an ISO sees exactly
 * what they were given, and unassigning them ends it in the same instant,
 * with no cache and no grace period.
 */
function isoMaySeeApplication(app, isoAffiliateId) {
    if (!app?.assignedIsoId || !isoAffiliateId)
        return false;
    return app.assignedIsoId === isoAffiliateId;
}
/**
 * What the tenant is waiting on, in the order they should act.
 *
 * Returns the documents THEY can do something about — a rejected one first,
 * because it is already late, then anything merely requested. An accepted or
 * uploaded document is not the tenant's problem any more and is left out.
 */
function tenantOutstandingDocuments(app) {
    const docs = app?.documents || [];
    const rank = (d) => (d.status === 'rejected' ? 0 : 1);
    return docs
        .filter((d) => d.status === 'requested' || d.status === 'rejected')
        .sort((a, b) => rank(a) - rank(b));
}
/**
 * The messages one viewer may read.
 *
 * The ONE place the audience rule is applied — every surface calls this and
 * none re-implements it, for the same reason `isoMaySeeApplication` exists.
 * A viewer always sees what they wrote themselves, which is what makes an
 * internal note (audience `['super_admin']`) legible to its author and
 * invisible to everyone else.
 *
 * `isoAffiliateId` matters: a partner sees the thread only while the
 * application is still theirs. Unassign them and the conversation goes with
 * it, in the same instant, with no grace period.
 */
function visibleMessages(app, viewer) {
    const thread = app?.thread || [];
    if (viewer.role === 'super_admin')
        return [...thread];
    if (viewer.role === 'iso' && !isoMaySeeApplication(app, viewer.isoAffiliateId))
        return [];
    return thread.filter((m) => m.authorRole === viewer.role || (m.audience || []).includes(viewer.role));
}
/**
 * The quote that is actually on the table, or none.
 *
 * Newest first by upload time, ignoring anything superseded. A record can
 * carry several — a partner who re-quotes after a decline leaves the old one
 * on the record, because "they came back with a better rate" is history worth
 * keeping — but only one is ever live.
 */
function activeQuote(app) {
    return (app?.quoteDocuments || [])
        .filter((q) => q.status !== 'superseded')
        .sort((a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0))[0];
}
/** Is a rate schedule sitting with the merchant, unanswered? */
function quoteAwaitingTenant(app) {
    return activeQuote(app)?.status === 'proposed';
}
/**
 * Whether this role may add a message with this audience.
 *
 * A tenant cannot write to the ISO behind RubyTill's back, and cannot write a
 * note RubyTill will never see — every message they send is visible to the
 * super admin, because RubyTill is accountable for the relationship. An ISO
 * likewise always writes within the super admin's sight. Only the super admin
 * may address one party without the other.
 */
function mayAddressAudience(authorRole, audience) {
    const to = new Set(audience.filter((a) => a !== authorRole));
    if (authorRole === 'super_admin')
        return { ok: true };
    if (!to.has('super_admin')) {
        return {
            ok: false,
            reason: 'Every message on an application is visible to RubyTill — the super admin cannot be left off the audience.',
        };
    }
    if (authorRole === 'tenant' && to.has('iso')) {
        // Deliberately allowed: the merchant talking to their own partner is the
        // normal case, and the super admin is on it either way by the rule above.
        return { ok: true };
    }
    if (authorRole === 'iso' && to.has('tenant'))
        return { ok: true };
    return { ok: true };
}
//# sourceMappingURL=merchantApplication.js.map